import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { BarChart3, Bell, Download, FileText, LayoutDashboard, LogOut, Megaphone, Menu, MessageCircle, Package, PackageMinus, PieChart, Receipt, Repeat, Sparkles, Tag, Tags, Target, Truck, UserCircle, Users, Flower2, Warehouse } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/movimenti", label: "Movimenti", icon: Receipt },
  { to: "/spese-ricorrenti", label: "Spese ricorrenti", icon: Repeat },
  { to: "/spese-brand", label: "Spese brand", icon: Megaphone },
  { to: "/prodotti", label: "Prodotti", icon: Package },
  { to: "/marche", label: "Marche", icon: Tag, admin: true },
  { to: "/vendite-mensili", label: "Vendite mensili", icon: BarChart3 },
  { to: "/fatture", label: "Fatture acquisto", icon: FileText, prefix: true },
  { to: "/fornitori", label: "Fornitori", icon: Truck },
  { to: "/magazzino", label: "Magazzino", icon: Warehouse, prefix: true },
  { to: "/uscite-merce", label: "Uscite merce", icon: PackageMinus },
  { to: "/analisi-margini", label: "Analisi margini", icon: PieChart, admin: true },
  { to: "/budget", label: "Budget", icon: Target, admin: true, badge: true },
  { to: "/consulente", label: "Consulente AI", icon: Sparkles, admin: true },
  { to: "/consulente/chat", label: "Chiedi al consulente", icon: MessageCircle, admin: true },
  { to: "/esporta", label: "Esporta", icon: Download, admin: true },
  { to: "/categorie", label: "Categorie", icon: Tags, admin: true },
  { to: "/utenti", label: "Utenti", icon: Users, admin: true },
];

const Brand = () => (
  <div className="flex items-center gap-3 px-6 pt-8 pb-10">
    <span className="h-10 w-10 rounded-full bg-[#E8C5C8]/60 grid place-items-center">
      <Flower2 className="h-5 w-5 text-[#8C6D62]" />
    </span>
    <div>
      <div className="font-display text-lg leading-tight text-[#2C2623]">Cosmetica Control</div>
      <div className="text-[11px] text-[#756B64] tracking-wide">Controllo di gestione</div>
    </div>
  </div>
);

function NavList({ onNavigate, alerts }) {
  const { isAdmin, user, logout } = useAuth();
  return (
    <div className="flex flex-col h-full">
      <Brand />
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {NAV.filter((n) => !n.admin || isAdmin).map(({ to, label, icon: Icon, badge, prefix }) => (
          <NavLink
            key={to}
            to={to}
            end={!prefix}
            onClick={onNavigate}
            data-testid={`sidebar-nav-${to.slice(1).replace("/", "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors duration-200 ${
                isActive ? "bg-[#E8DED6] text-[#2C2623] font-semibold" : "text-[#756B64] hover:bg-[#EDE5DE] hover:text-[#2C2623]"
              }`
            }
          >
            <Icon className="h-4 w-4" strokeWidth={1.8} />
            {label}
            {badge && alerts > 0 && <span data-testid="sidebar-budget-badge" className="ml-auto text-[10px] font-bold min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-[#C0573F] text-white">{alerts}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="m-3 p-4 rounded-2xl bg-white/70 border border-[#EAE3DC]">
        <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
        <div className="text-xs text-[#756B64] truncate">{user?.email}</div>
        <div className="flex items-center justify-between mt-3">
          <span data-testid="sidebar-user-role" className={`text-[11px] px-2 py-0.5 rounded-full ${isAdmin ? "bg-[#E8C5C8]/60 text-[#77594E]" : "bg-[#8AA090]/20 text-[#4E6656]"}`}>
            {isAdmin ? "Amministratore" : "Collaboratore"}
          </span>
          <div className="flex items-center gap-3">
            <NavLink to="/profilo" onClick={onNavigate} data-testid="sidebar-profilo-link" className="text-xs text-[#756B64] hover:text-[#8C6D62] flex items-center gap-1 transition-colors"><UserCircle className="h-3.5 w-3.5" />Profilo</NavLink>
            <button data-testid="logout-button" onClick={logout} className="text-xs text-[#756B64] hover:text-[#8C6D62] flex items-center gap-1 transition-colors">
              <LogOut className="h-3.5 w-3.5" /> Esci
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState(0);
  const { isAdmin } = useAuth();
  const { pathname } = useLocation();

  useEffect(() => {
    if (isAdmin) api.get("/budget/alerts").then((r) => setAlerts(r.data.count)).catch(() => setAlerts(0));
  }, [isAdmin, pathname]);

  return (
    <div className="min-h-screen bg-[#FAF7F5]">
      <aside className="hidden md:flex w-64 fixed inset-y-0 left-0 flex-col border-r border-[#EAE3DC] bg-[#F4EFEA]" data-testid="sidebar">
        <NavList alerts={alerts} />
      </aside>
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 bg-[#F4EFEA]/90 backdrop-blur border-b border-[#EAE3DC]">
        <span className="font-display text-base">Cosmetica Control</span>
        <div className="flex items-center gap-1">
          {alerts > 0 && (
            <NavLink to="/budget" data-testid="header-alerts-badge" className="relative p-2 rounded-lg hover:bg-[#E8DED6]">
              <Bell className="h-5 w-5" />
              <span className="absolute top-0.5 right-0.5 text-[9px] font-bold h-4 min-w-[16px] px-1 grid place-items-center rounded-full bg-[#C0573F] text-white">{alerts}</span>
            </NavLink>
          )}
          <button data-testid="mobile-menu-button" onClick={() => setOpen(true)} className="p-2 rounded-lg hover:bg-[#E8DED6]">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-[#F4EFEA]" data-testid="mobile-menu" aria-describedby={undefined}>
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <NavList onNavigate={() => setOpen(false)} alerts={alerts} />
        </SheetContent>
      </Sheet>
      <main className="md:pl-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-10 py-8 sm:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
